import { subscriptionManifest } from './campaignGoals/registry';

export const GOAL_PLUGIN = Object.freeze({
  version: "v1",
  goalType: subscriptionManifest.pluginKey,
});

export const CLIENT_ONBOARDING_DRAFT_PREFIX = "proovam_client_onboarding_draft_v3:";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_MONEY = 1_000_000_000;
const REQUEST_TYPES = new Set(["", "direct", "broadcast"]);
const DIRECT_VERSIONS = new Set(["", "v1", "v2"]);
const INTEGRATION_METHODS = new Set(["api", "webhook"]);
const AUTH_TYPES = new Set(["none", "jwt", "api_key"]);
const IDENTIFIER_TYPES = new Set(["phone", "email", "custom"]);

const createUuid = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const DEFAULT_DRAFT = Object.freeze({
  projectMode: "create",
  projectId: "",
  projectName: "",
  projectDescription: "",
  requestType: "",
  directVersion: "",
  name: "",
  description: "",
  productUrl: "",
  goalType: subscriptionManifest.pluginKey,
  subscriptionAmount: "50000",
  commissionPerGoal: "5000",
  integrationMethod: "api",
  verificationUrl: "",
  registrationUrl: "",
  authType: "none",
  userIdentifierType: "phone",
  referralEnabled: false,
  productType: "web_app",
  audiencePayEnabled: false,
  audiencePayModel: "performance_triggered_media_fee",
});

export const getClientOnboardingDraftKey = (clientId) => (
  `${CLIENT_ONBOARDING_DRAFT_PREFIX}${encodeURIComponent(String(clientId || "anonymous"))}`
);

export const clearClientOnboardingDrafts = (storage = globalThis.localStorage) => {
  if (!storage) return;
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (key?.startsWith(CLIENT_ONBOARDING_DRAFT_PREFIX)) storage.removeItem(key);
  }
};

export const createProveRequestDraft = (overrides = {}) => {
  const merged = { ...DEFAULT_DRAFT, ...overrides };
  return {
    ...merged,
    requestType: REQUEST_TYPES.has(merged.requestType) ? merged.requestType : "",
    directVersion: DIRECT_VERSIONS.has(merged.directVersion) ? merged.directVersion : "",
    integrationMethod: INTEGRATION_METHODS.has(merged.integrationMethod) ? merged.integrationMethod : "api",
    authType: AUTH_TYPES.has(merged.authType) ? merged.authType : "none",
    userIdentifierType: IDENTIFIER_TYPES.has(merged.userIdentifierType) ? merged.userIdentifierType : "phone",
    idempotencyKey: UUID_PATTERN.test(merged.idempotencyKey || "")
      ? merged.idempotencyKey
      : createUuid(),
    projectIdempotencyKey: UUID_PATTERN.test(merged.projectIdempotencyKey || "")
      ? merged.projectIdempotencyKey
      : createUuid(),
    projectMode: merged.projectMode === "existing" ? "existing" : "create",
    goalType: subscriptionManifest.pluginKey,
  };
};

const httpsUrlError = (value) => {
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "https:" || !parsed.hostname || parsed.username || parsed.password) {
      return "must be a valid HTTPS URL without embedded credentials";
    }
    if (parsed.search || parsed.hash) {
      return "must not contain query parameters or fragments; add credentials after creation";
    }
    return null;
  } catch {
    return "must be a valid HTTPS URL";
  }
};

const moneyError = (value, label) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0 || amount > MAX_MONEY) {
    return `${label} must be a valid amount between 0 and ${MAX_MONEY.toLocaleString()}`;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(String(value).trim())) {
    return `${label} can have at most 2 decimal places`;
  }
  return null;
};

export const validateProveRequestStage = (form, stage) => {
  const errors = {};

  if (stage === "offer") {
    if (form.projectMode === "create" && !form.projectId && (!form.projectName || form.projectName.trim().length < 3)) {
      errors.projectName = "Project name must be at least 3 characters";
    }
    if (form.projectMode === "existing" && !form.projectId) {
      errors.projectId = "Choose the Project that owns this Offer";
    }
    if (!form.name || form.name.trim().length < 3) {
      errors.name = "Offer name must be at least 3 characters";
    }
    const productUrlError = httpsUrlError(form.productUrl);
    if (productUrlError) errors.productUrl = `Product URL ${productUrlError}`;
  }

  if (stage === "request") {
    if (!REQUEST_TYPES.has(form.requestType) || !form.requestType) {
      errors.requestType = "Choose Direct or Broadcast Proove";
    } else if (form.requestType === "direct" && (!DIRECT_VERSIONS.has(form.directVersion) || !form.directVersion)) {
      errors.directVersion = "Choose Direct V1 or Direct V2";
    }
  }

  if (stage === "integration") {
    if (!INTEGRATION_METHODS.has(form.integrationMethod)) {
      errors.integrationMethod = "Choose API or Webhook integration";
    }
    if (!AUTH_TYPES.has(form.authType)) errors.authType = "Choose a supported authentication type";
    const verificationUrlError = httpsUrlError(form.verificationUrl);
    if (verificationUrlError) errors.verificationUrl = `Verification URL ${verificationUrlError}`;
    if (form.requestType === "direct" && form.directVersion === "v2") {
      if (!form.registrationUrl) {
        errors.registrationUrl = "Registration URL is required for Direct V2";
      } else {
        const registrationUrlError = httpsUrlError(form.registrationUrl);
        if (registrationUrlError) errors.registrationUrl = `Registration URL ${registrationUrlError}`;
      }
    }
  }

  if (stage === "economics") {
    if (form.goalType !== subscriptionManifest.pluginKey) {
      errors.goalType = "Goal-plugin V1 supports Subscription only";
    }
    const subscriptionError = moneyError(form.subscriptionAmount, "Subscription amount");
    if (subscriptionError) errors.subscriptionAmount = subscriptionError;
    const commissionError = moneyError(form.commissionPerGoal, "Commission");
    if (commissionError) errors.commissionPerGoal = commissionError;
    if (!IDENTIFIER_TYPES.has(form.userIdentifierType)) {
      errors.userIdentifierType = "Choose a supported customer identifier";
    }
  }

  return errors;
};

export const validateProveRequestForm = (form) => (
  ["offer", "request", "integration", "economics"].reduce(
    (allErrors, stage) => ({ ...allErrors, ...validateProveRequestStage(form, stage) }),
    {},
  )
);

export const buildProductCreatePayload = (form) => {
  const isBroadcast = form.requestType === "broadcast";
  const isDirectV2 = form.requestType === "direct" && form.directVersion === "v2";
  const integrationMethod = isBroadcast ? "webhook" : form.integrationMethod;
  const identifierField = form.userIdentifierType === "email"
    ? "email"
    : form.userIdentifierType === "custom"
      ? "custom_id"
      : "phone_number";

  return {
    idempotencyKey: form.idempotencyKey,
    projectId: form.projectId,
    name: form.name.trim(),
    description: form.description?.trim() || null,
    commission_rate: 0.10,
    subscription_amount: Number(form.subscriptionAmount),
    productUrl: form.productUrl,
    apiEndpoint: integrationMethod === "api" ? form.verificationUrl : null,
    webhookUrl: integrationMethod === "webhook" ? form.verificationUrl : null,
    verificationUrl: form.verificationUrl,
    commissionPerGoal: Number(form.commissionPerGoal),
    goalType: subscriptionManifest.pluginKey,
    userIdentifierType: form.userIdentifierType,
    userIdentifierField: identifierField,
    faceToFaceEnabled: !isBroadcast,
    referralEnabled: isBroadcast || Boolean(form.referralEnabled),
    productType: isBroadcast || form.referralEnabled ? form.productType : null,
    integrationMethod,
    requestType: form.requestType,
    directVersion: form.requestType === "direct" ? form.directVersion : null,
    registrationUrl: isDirectV2 ? form.registrationUrl : null,
    audiencePayEnabled: isBroadcast && Boolean(form.audiencePayEnabled),
    audiencePayModel: isBroadcast && form.audiencePayEnabled ? form.audiencePayModel : null,
  };
};
